// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * S-expression serializer (spec §6.1).
 *
 * Output must be loadable by KiCad; byte-identical formatting to KiCad's own
 * output is explicitly not required. We follow KiCad's general layout — one node
 * per line, two-space indent — so that output produces readable git diffs.
 *
 * Layout rule: a node whose children are all atoms is emitted inline
 * (`(width 0.2)`); a node with any child node is emitted across lines. This
 * matches KiCad closely enough to keep diffs small.
 */

import { needsQuoting, type Node, type SexpChild } from './types.ts';

export interface SerializeOptions {
  /** Indent unit. Defaults to two spaces. */
  indent?: string;
  /** Trailing newline at end of output. Defaults to true. */
  trailingNewline?: boolean;
}

export function serialize(root: Node, options: SerializeOptions = {}): string {
  const indent = options.indent ?? '  ';
  const out: string[] = [];
  writeNode(out, root, 0, indent);
  const text = out.join('');
  return options.trailingNewline === false ? text : text + '\n';
}

function writeNode(out: string[], n: Node, depth: number, indent: string): void {
  const pad = indent.repeat(depth);
  const hasChildNodes = n.children.some((c) => c.kind === 'node');

  if (!hasChildNodes) {
    out.push(pad + inlineNode(n));
    return;
  }

  out.push(pad + '(' + n.name);
  for (const child of n.children) {
    out.push('\n');
    if (child.kind === 'atom') {
      out.push(indent.repeat(depth + 1) + atomText(child.value, child.quoted));
    } else {
      writeNode(out, child, depth + 1, indent);
    }
  }
  out.push('\n' + pad + ')');
}

function inlineNode(n: Node): string {
  const parts: string[] = [];
  if (n.name !== '') parts.push(n.name);
  for (const child of n.children) {
    parts.push(atomText((child as { value: string }).value, (child as { quoted: boolean }).quoted));
  }
  return '(' + parts.join(' ') + ')';
}

function atomText(value: string, quoted: boolean): string {
  if (!quoted && !needsQuoting(value)) return value;
  return '"' + escapeString(value) + '"';
}

export function escapeString(value: string): string {
  let out = '';
  for (const ch of value) {
    switch (ch) {
      case '\\':
        out += '\\\\';
        break;
      case '"':
        out += '\\"';
        break;
      case '\n':
        out += '\\n';
        break;
      case '\t':
        out += '\\t';
        break;
      case '\r':
        out += '\\r';
        break;
      default:
        out += ch;
    }
  }
  return out;
}

/**
 * Compact single-line form. Used for hashing, normalised comparison and error
 * messages — never for file output.
 */
export function serializeCompact(x: SexpChild): string {
  if (x.kind === 'atom') return atomText(x.value, x.quoted);
  const parts: string[] = [];
  if (x.name !== '') parts.push(x.name);
  for (const child of x.children) parts.push(serializeCompact(child));
  return '(' + parts.join(' ') + ')';
}
